/**
 * Hook for document search + pagination: uses documentSearchSlice and API (api/get-search).
 * Single source for table data and pagination. Pagination uses the same API via goToPage(pageIndex).
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  searchDocumentsThunk,
  fetchSearchCountThunk,
  clearSearch,
  setPageSize as setPageSizeAction,
  requestRefreshAllDocuments,
  markAllDocumentsStaleAfterCheckout,
  clearPendingAllDocumentsRefresh,
} from "../slices/documents/documentSearchSlice";

function selectDocumentSearch(state) {
  return state.DocumentSearch ?? {};
}

export function useDocumentSearch() {
  const dispatch = useAppDispatch();
  const {
    loading,
    countLoading,
    data,
    isError,
    message,
    selectedPageIndex,
    pageSize,
    totalCount,
    refreshTrigger,
    lastSearchParams,
    pendingRefreshOnNextMount,
  } = useAppSelector(selectDocumentSearch);

  const searchDocuments = useCallback(
    (params) => dispatch(searchDocumentsThunk(params)),
    [dispatch]
  );

  /** Fetch total count from api/get-search-records-count. Call with same params as search (e.g. after search). */
  const fetchSearchCount = useCallback(
    (params) => dispatch(fetchSearchCountThunk(params)),
    [dispatch]
  );

  /** Load a specific page using last search params. Call after at least one search. */
  const goToPage = useCallback(
    (pageIndex) => dispatch(searchDocumentsThunk(pageIndex)),
    [dispatch]
  );

  const clearSearchAction = useCallback(() => dispatch(clearSearch()), [dispatch]);

  const setPageSize = useCallback(
    (size) => dispatch(setPageSizeAction(size)),
    [dispatch]
  );

  const requestRefreshAllDocumentsAction = useCallback(
    () => dispatch(requestRefreshAllDocuments()),
    [dispatch]
  );

  const markAllDocumentsStaleAfterCheckoutAction = useCallback(
    () => dispatch(markAllDocumentsStaleAfterCheckout()),
    [dispatch]
  );

  const clearPendingAllDocumentsRefreshAction = useCallback(
    () => dispatch(clearPendingAllDocumentsRefresh()),
    [dispatch]
  );

  const documents = Array.isArray(data) ? data : (data?.value ?? []);

  /** Pagination result in API-like shape: { value: items[], totalCount } (totalCount from get-search-records-count). */
  const paginationResult = {
    value: documents,
    totalCount,
  };

  return {
    searchDocuments,
    fetchSearchCount,
    goToPage,
    loading,
    countLoading,
    data,
    documents,
    paginationResult,
    isError,
    message,
    clearSearch: clearSearchAction,
    setPageSize,
    selectedPageIndex,
    pageSize,
    searchResultCount: totalCount,
    refreshTrigger: refreshTrigger ?? 0,
    lastSearchParams: lastSearchParams ?? null,
    requestRefreshAllDocuments: requestRefreshAllDocumentsAction,
    pendingRefreshOnNextMount: pendingRefreshOnNextMount === true,
    markAllDocumentsStaleAfterCheckout: markAllDocumentsStaleAfterCheckoutAction,
    clearPendingAllDocumentsRefresh: clearPendingAllDocumentsRefreshAction,
  };
}
